'use client'

import {
  Button,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
  useSidebar,
} from '@repo/ui'
import { cn } from '@repo/ui/lib'
import { Minus, Plus } from 'lucide-react'
import { ChangeEvent } from 'react'
import { Control } from 'react-hook-form'
import { z } from 'zod'

const WEIGHT_OPTIONS = [
  { label: 'tonnes', multiplier: 1, value: 't' },
  { label: 'kilograms', multiplier: 1000, value: 'kg' },
]

const weightInputParser = ({ value }: { value: string }) =>
  z
    .string()
    .transform(value => {
      const [valueValue, valueType] = value.trim().split(' ')
      const parsedNumber = z.coerce.number().parse(valueValue)
      const foundType =
        WEIGHT_OPTIONS.find(weight => weight.value === valueType) || WEIGHT_OPTIONS[0]

      return {
        type: foundType,
        value: isNaN(parsedNumber) ? 0 : parsedNumber,
      }
    })
    .parse(value)

interface Props {
  control: Control<{ weight: string }>
}

export const WeightInput = ({ control }: Props) => {
  const { open } = useSidebar()

  return (
    <FormField
      control={control}
      name={'weight'}
      render={({ field }) => {
        const parsedValue = weightInputParser({ value: field.value || '0 t' })

        const handlePlus = () => {
          field.onChange(`${parsedValue.value + 1} ${parsedValue.type.value}`)
        }

        const handleMinus = () => {
          if (parsedValue.value > 0) {
            field.onChange(`${parsedValue.value - 1} ${parsedValue.type.value}`)
          }
        }

        const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
          const inputValue = event.target.value
          const newValue =
            inputValue === ''
              ? `0 ${parsedValue.type.value}`
              : `${inputValue} ${parsedValue.type.value}`
          field.onChange(newValue)
        }

        const selectChange = (value: string) => {
          if (parsedValue.type.value === 'kg' && value === 't') {
            field.onChange(`${parsedValue.value / 1000} t`)
          } else if (parsedValue.type.value === 't' && value === 'kg') {
            field.onChange(`${parsedValue.value * 1000} kg`)
          }
        }

        return (
          <FormItem>
            <FormLabel className={'text-card-foreground'}>Weight</FormLabel>
            <FormControl>
              <div className={cn('flex items-center rounded border', open && 'max-lg:flex-col')}>
                <div
                  className={cn('flex w-6/12 shrink-0 items-center gap-1', open && 'max-lg:w-full')}
                >
                  <Button
                    className={'shrink-0'}
                    onClick={handleMinus}
                    size={'icon'}
                    type={'button'}
                    variant={'ghost'}
                  >
                    <Minus />
                  </Button>
                  <Input
                    className={'min-w-10 rounded-none border-none px-0 text-center'}
                    onChange={handleInputChange}
                    placeholder={'0'}
                    type="number"
                    value={parsedValue.value.toString()}
                  />
                  <Button
                    className={'shrink-0'}
                    onClick={handlePlus}
                    size={'icon'}
                    type={'button'}
                    variant={'ghost'}
                  >
                    <Plus />
                  </Button>
                </div>
                <Separator
                  className={cn('hidden', open && 'max-lg:block')}
                  orientation={'horizontal'}
                />
                <Select onValueChange={selectChange} value={parsedValue.type.value}>
                  <SelectTrigger
                    className={
                      'w-full rounded-l-none border-none ring-transparent ring-offset-transparent'
                    }
                  >
                    <SelectValue placeholder="Select unit" />
                  </SelectTrigger>
                  <SelectContent>
                    {WEIGHT_OPTIONS.map((weight, index) => (
                      <SelectItem key={index} value={weight.value}>
                        {weight.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>
        )
      }}
    />
  )
}
